import { Role, type UserListItem } from "@pilatestime-admin/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Pencil, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchUsersQueryOptions, useUpdateUserRole } from "@/api/users";
import { DataTable } from "@/components/data-table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useIsSuperAdmin } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";

export const Route = createFileRoute("/_auth/app/_app/settings/admin-users/")({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		// Preload users data
		context.queryClient.ensureQueryData(fetchUsersQueryOptions(token));

		return { userId, token };
	},
	component: AdminUsersPage,
});

// Available roles for selection
const AVAILABLE_ROLES = [
	Role.SUPER_ADMIN,
	Role.SUPPORT_ADMIN,
	Role.ANALYTICS_VIEWER,
	Role.USER_MANAGER,
];

function AdminUsersPage() {
	const { t } = useTranslation();
	const { token } = Route.useLoaderData();
	const isSuperAdmin = useIsSuperAdmin();
	const updateUserRole = useUpdateUserRole();

	// State for edit role dialog
	const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
	const [selectedRole, setSelectedRole] = useState<Role | null>(null);
	const [isDialogOpen, setIsDialogOpen] = useState(false);

	const {
		data: usersData,
		isLoading,
		error,
	} = useSuspenseQuery(fetchUsersQueryOptions(token));

	const handleSaveRole = () => {
		if (!selectedUser || !selectedRole) return;

		updateUserRole.mutate(
			{
				token,
				userId: selectedUser.id,
				data: { role: selectedRole },
			},
			{
				onSuccess: () => {
					setIsDialogOpen(false);
					setSelectedUser(null);
					setSelectedRole(null);
				},
			},
		);
	};

	const handleCloseDialog = () => {
		setIsDialogOpen(false);
		setSelectedUser(null);
		setSelectedRole(null);
	};

	const columns = useMemo<ColumnDef<UserListItem>[]>(
		() => [
			{
				accessorKey: "email",
				header: t("users.table.email"),
				cell: ({ row }) => (
					<div className="flex items-center gap-3">
						{row.original.imageUrl ? (
							<img
								src={row.original.imageUrl}
								alt=""
								className="w-8 h-8 rounded-full"
							/>
						) : (
							<div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
								<span className="text-primary text-sm font-medium">
									{row.original.email.charAt(0).toUpperCase()}
								</span>
							</div>
						)}
						<span className="font-medium">{row.original.email}</span>
					</div>
				),
			},
			{
				accessorKey: "firstName",
				header: t("users.table.name"),
				cell: ({ row }) => {
					const firstName = row.original.firstName || "";
					const lastName = row.original.lastName || "";
					const fullName = `${firstName} ${lastName}`.trim();
					return fullName || "-";
				},
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "role",
				header: t("users.table.role"),
				cell: ({ row }) => (
					<span className="badge badge-outline badge-sm">
						{t(`users.roles.${row.original.role.toLowerCase()}`)}
					</span>
				),
			},
			{
				accessorKey: "isActive",
				header: t("users.table.status"),
				cell: ({ row }) => (
					<span
						className={`badge badge-sm ${row.original.isActive ? "badge-success" : "badge-error"}`}
					>
						{row.original.isActive
							? t("users.status.active")
							: t("users.status.inactive")}
					</span>
				),
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "createdAt",
				header: t("users.table.createdAt"),
				cell: ({ row }) => {
					const date = new Date(row.original.createdAt);
					return date.toLocaleDateString();
				},
				meta: { hiddenOnMobile: true },
			},
			// Actions column (only for SUPER_ADMIN)
			...(isSuperAdmin
				? [
						{
							id: "actions",
							header: t("users.table.actions"),
							cell: ({ row }: { row: { original: UserListItem } }) => (
								<button
									type="button"
									className="btn btn-ghost btn-sm"
									onClick={() => {
										if (!isSuperAdmin) return;
										setSelectedUser(row.original);
										setSelectedRole(row.original.role);
										setIsDialogOpen(true);
									}}
									title={t("users.editRole.title")}
								>
									<Pencil className="w-4 h-4" />
								</button>
							),
						},
					]
				: []),
		],
		[t, isSuperAdmin],
	);

	return (
		<div className="space-y-6" data-testid="admin-users-page">
			{/* Users Table */}
			<div className="card bg-base-100 shadow-sm">
				<div className="card-body p-0">
					<DataTable
						data={usersData?.users || []}
						columns={columns}
						isLoading={isLoading}
						error={error}
						testId="admin-users"
						emptyState={{
							title: t(
								"settings.adminUsers.empty.title",
								"No admin users found",
							),
							description: t(
								"settings.adminUsers.empty.description",
								"There are no admin users to display.",
							),
							icon: Users,
						}}
					/>
				</div>
			</div>

			{/* Edit Role Dialog */}
			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("users.editRole.title")}</DialogTitle>
						<DialogDescription>
							{t("users.editRole.description", {
								email: selectedUser?.email,
							})}
						</DialogDescription>
					</DialogHeader>

					<div className="py-4">
						<span className="block text-sm font-medium mb-2">
							{t("users.editRole.selectRole")}
						</span>
						<Select
							value={selectedRole || undefined}
							onValueChange={(value) => setSelectedRole(value as Role)}
						>
							<SelectTrigger
								className="w-full"
								aria-label={t("users.editRole.selectRole")}
							>
								<SelectValue placeholder={t("users.editRole.selectRole")} />
							</SelectTrigger>
							<SelectContent>
								{AVAILABLE_ROLES.map((role) => (
									<SelectItem key={role} value={role}>
										{t(`users.roles.${role.toLowerCase()}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<DialogFooter>
						<button
							type="button"
							className="btn btn-ghost"
							onClick={handleCloseDialog}
						>
							{t("common.cancel")}
						</button>
						<button
							type="button"
							className="btn btn-primary"
							onClick={handleSaveRole}
							disabled={
								updateUserRole.isPending || selectedRole === selectedUser?.role
							}
						>
							{updateUserRole.isPending ? (
								<span className="loading loading-spinner loading-sm" />
							) : (
								t("common.save")
							)}
						</button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
